/**
 * Feature Flags - Shared Configuration
 *
 * Centralized feature flag management for StreamSyncEngage SaaS platform.
 * This module is shared between:
 * - Service API (Node.js backend)
 * - Client SaaS (Vite frontend)
 * - Any future microservices
 *
 * Usage:
 *
 * Backend (CommonJS):
 *   const { FEATURE_FLAGS } = require('../../../shared/featureFlags.js');
 *   localStorage.setItem(FEATURE_FLAGS.FF_SSE_LS_SESSION_USER, data);
 *
 * Frontend (ES Modules):
 *   import { FEATURE_FLAGS } from '../../../shared/featureFlags.js';
 *   localStorage.setItem(FEATURE_FLAGS.FF_SSE_LS_SESSION_USER, data);
 */

/**
 * Feature Flags Registry
 *
 * Naming Convention:
 * - FF_<SCOPE>_<CATEGORY>_<NAME>
 * - SCOPE: SSE (StreamSyncEngage), CORE (Core functionality), UI (User Interface)
 * - CATEGORY: LS (localStorage), API (API), AUTH (Authentication), etc.
 * - NAME: Descriptive feature name in UPPER_SNAKE_CASE
 *
 * @type {Readonly<Object>}
 */
const FEATURE_FLAGS = Object.freeze({
  // ============================================
  // LocalStorage Keys
  // ============================================

  /**
   * Session user entity storage key
   * @type {string}
   * @default "mvv_session_user"
   */
  FF_SSE_LS_SESSION_USER: 'mvv_session_user',

  /**
   * JWT token storage key (managed by mvvLegit)
   * @type {string}
   * @default "mvv"
   * @readonly
   */
  FF_SSE_LS_TOKEN: 'mvv',

  /**
   * User preferences storage key
   * @type {string}
   * @default "mvv_user_prefs"
   */
  FF_SSE_LS_USER_PREFS: 'mvv_user_prefs',

  /**
   * Application theme storage key
   * @type {string}
   * @default "mvv_theme"
   */
  FF_SSE_LS_THEME: 'mvv_theme',

  /**
   * Last visited route storage key
   * @type {string}
   * @default "mvv_last_route"
   */
  FF_SSE_LS_LAST_ROUTE: 'mvv_last_route',

  // ============================================
  // API Configuration
  // ============================================

  /**
   * Enable API request logging
   * @type {boolean}
   * @default false
   */
  FF_SSE_API_DEBUG_LOGGING: false,

  /**
   * API request timeout in milliseconds
   * @type {number}
   * @default 30000
   */
  FF_SSE_API_TIMEOUT: 30000,

  /**
   * Maximum retry attempts for failed API requests
   * @type {number}
   * @default 3
   */
  FF_SSE_API_MAX_RETRIES: 3,

  /**
   * Enable API response caching
   * @type {boolean}
   * @default true
   */
  FF_SSE_API_CACHE_ENABLED: true,

  // ============================================
  // Authentication Configuration
  // ============================================

  /**
   * JWT token expiry time in hours
   * @type {number}
   * @default 6
   */
  FF_SSE_AUTH_TOKEN_EXPIRY_HOURS: 6,

  /**
   * Enable automatic token refresh
   * @type {boolean}
   * @default false
   */
  FF_SSE_AUTH_AUTO_REFRESH: false,

  /**
   * Session timeout warning threshold in minutes
   * @type {number}
   * @default 5
   */
  FF_SSE_AUTH_SESSION_WARNING_MINUTES: 5,

  /**
   * Enable "Remember Me" functionality
   * @type {boolean}
   * @default true
   */
  FF_SSE_AUTH_REMEMBER_ME: true,

  /**
   * Maximum failed login attempts before lockout
   * @type {number}
   * @default 5
   */
  FF_SSE_AUTH_MAX_LOGIN_ATTEMPTS: 5,

  // ============================================
  // UI/UX Configuration
  // ============================================

  /**
   * Enable toast notifications
   * @type {boolean}
   * @default true
   */
  FF_SSE_UI_TOAST_ENABLED: true,

  /**
   * Toast notification duration in milliseconds
   * @type {number}
   * @default 5000
   */
  FF_SSE_UI_TOAST_DURATION: 5000,

  /**
   * Enable loading spinners
   * @type {boolean}
   * @default true
   */
  FF_SSE_UI_LOADING_SPINNERS: true,

  /**
   * Enable dark mode
   * @type {boolean}
   * @default false
   */
  FF_SSE_UI_DARK_MODE: false,

  /**
   * Enable animations
   * @type {boolean}
   * @default true
   */
  FF_SSE_UI_ANIMATIONS: true,

  // ============================================
  // Feature Toggles
  // ============================================

  /**
   * Enable signup functionality
   * @type {boolean}
   * @default true
   */
  FF_SSE_FEATURE_SIGNUP_ENABLED: true,

  /**
   * Enable password reset functionality
   * @type {boolean}
   * @default true
   */
  FF_SSE_FEATURE_PASSWORD_RESET_ENABLED: true,

  /**
   * Enable email verification
   * @type {boolean}
   * @default true
   */
  FF_SSE_FEATURE_EMAIL_VERIFICATION_ENABLED: true,

  /**
   * Enable user profile editing
   * @type {boolean}
   * @default true
   */
  FF_SSE_FEATURE_PROFILE_EDIT_ENABLED: true,

  /**
   * Enable analytics tracking
   * @type {boolean}
   * @default false
   */
  FF_SSE_FEATURE_ANALYTICS_ENABLED: false,

  /**
   * Run a constrained routing pass before each prompt, asking the model whether a
   * registered template answers it better than prose.
   *
   * Costs a second short generation per prompt - WebLLM's KV cache does not
   * carry across a differing message set. Turn off to send every prompt straight
   * to prose; templates remain reachable via `::template render`.
   *
   * @type {boolean}
   * @default true
   */
  FF_CLIENT_TEMPLATE_ROUTING: true,

  // ============================================
  // Performance Configuration
  // ============================================

  /**
   * Silo cache TTL in milliseconds
   * @type {number}
   * @default 300000 (5 minutes)
   */
  FF_SSE_CACHE_TTL_MS: 300000,

  /**
   * Enable response compression
   * @type {boolean}
   * @default true
   */
  FF_SSE_COMPRESSION_ENABLED: true,

  /**
   * Maximum JSON payload size in MB
   * @type {number}
   * @default 50
   */
  FF_SSE_MAX_PAYLOAD_SIZE_MB: 50,

  // ============================================
  // Debugging & Development
  // ============================================

  /**
   * Enable verbose console logging
   * @type {boolean}
   * @default false
   */
  FF_SSE_DEBUG_VERBOSE: false,

  /**
   * Enable Redux DevTools integration
   * @type {boolean}
   * @default false
   */
  FF_SSE_DEBUG_REDUX_DEVTOOLS: false,

  /**
   * Enable performance monitoring
   * @type {boolean}
   * @default false
   */
  FF_SSE_DEBUG_PERFORMANCE: false,

  /**
   * Bypass authentication (DEVELOPMENT ONLY)
   * @type {boolean}
   * @default false
   * @warning NEVER enable in production
   */
  FF_SSE_DEBUG_BYPASS_AUTH: false,

  // ============================================
  // Client Configuration (LaunchLegitController)
  // ============================================

  /**
   * Auth ping interval in milliseconds
   * How often to check if user's token is still valid
   * @type {number}
   * @default 1440000 (24 minutes)
   */
  FF_CLIENT_AUTH_PING_MS: 1440000,

  /**
   * Carousel element ID
   * @type {string}
   * @default "main_carousel_nav"
   */
  FF_CLIENT_CAROUSEL_ID: 'main_carousel_nav',

  /**
   * LocalStorage key for compressed entity state
   * @type {string}
   * @default "sse_entity"
   */
  FF_CLIENT_LS_SESSION_ENTITY: 'sse_entity',

  /**
   * LocalStorage key for deep-link GUID
   * @type {string}
   * @default "sse_deeplink"
   */
  FF_CLIENT_LS_DEEP_LINK: 'sse_deeplink',

  // ============================================
  // Route Names
  // ============================================

  /**
   * Sign-in route name
   * @type {string}
   * @default "signin_route"
   */
  FF_CLIENT_ROUTE_SIGNIN: 'signin_route',

  /**
   * Sign-up route name
   * @type {string}
   * @default "signup_route"
   */
  FF_CLIENT_ROUTE_SIGNUP: 'signup_route',

  /**
   * Home route name
   * @type {string}
   * @default "home_route"
   */
  FF_CLIENT_ROUTE_HOME: 'home_route',

  /**
   * Reset password (authenticated) route name
   * @type {string}
   * @default "resethash_route"
   */
  FF_CLIENT_ROUTE_RESET_HASH: 'resethash_route',

  /**
   * Reset password (forgot flow) route name
   * @type {string}
   * @default "resetforgot_route"
   */
  FF_CLIENT_ROUTE_RESET_FORGOT: 'resetforgot_route',

  /**
   * Sign-out route name
   * @type {string}
   * @default "signout_route"
   */
  FF_CLIENT_ROUTE_SIGNOUT: 'signout_route',

  /**
   * Error route name
   * @type {string}
   * @default "error_route"
   */
  FF_CLIENT_ROUTE_ERROR: 'error_route',

  /**
   * Email verification prompt route name
   * @type {string}
   * @default "verf_link_route"
   */
  FF_CLIENT_ROUTE_VERF_LINK: 'verf_link_route',

  /**
   * Offline status route name
   * @type {string}
   * @default "offline_route"
   */
  FF_CLIENT_ROUTE_OFFLINE: 'offline_route',

  /**
   * Protected routes (require authentication)
   * @type {Array<string>}
   */
  FF_CLIENT_PROTECTED_ROUTES: [
    'home_route',
    'resethash_route',
    'signout_route',
    'user_management_route'
  ],

  // ============================================
  // API Endpoints
  // ============================================

  /**
   * Sign-in endpoint path
   * @type {string}
   * @default "/m5t/v5/acctEntity/signin"
   */
  FF_CLIENT_ENDPOINT_SIGNIN: '/m5t/v5/acctEntity/signin',

  /**
   * Sign-out endpoint path
   * @type {string}
   * @default "/m5t/v5/acctEntity/signout"
   */
  FF_CLIENT_ENDPOINT_SIGNOUT: '/m5t/v5/acctEntity/signout',

  /**
   * Sign-up endpoint path
   * @type {string}
   * @default "/m5t/v5/acctEntity/"
   */
  FF_CLIENT_ENDPOINT_SIGNUP: '/m5t/v5/acctEntity/',

  /**
   * Reset password (authenticated) endpoint path
   * @type {string}
   * @default "/m5t/v5/acctEntity/resetHash"
   */
  FF_CLIENT_ENDPOINT_RESET_HASH: '/m5t/v5/acctEntity/resetHash',

  /**
   * Reset password (forgot flow) endpoint path
   * @type {string}
   * @default "/m5t/v5/acctEntity/resetforgot"
   */
  FF_CLIENT_ENDPOINT_RESET_FORGOT: '/m5t/v5/acctEntity/resetforgot',

  /**
   * Auth ping endpoint path
   * @type {string}
   * @default "/ping"
   */
  FF_CLIENT_ENDPOINT_PING: '/ping',

  /**
   * Metering endpoint path
   * @type {string}
   * @default "/m5t/v5/coreMeter/"
   */
  FF_CLIENT_ENDPOINT_METER: '/m5t/v5/coreMeter/',

  // ============================================
  // Metering Captions
  // ============================================

  /**
   * Deep-link access metering caption
   * @type {string}
   * @default "deep_link_access"
   */
  FF_CLIENT_METER_DEEP_LINK: 'deep_link_access',

  /**
   * Client-initiated signout metering caption
   * @type {string}
   * @default "signout_client_initiated"
   */
  FF_CLIENT_METER_SIGNOUT_CLIENT: 'signout_client_initiated',

  /**
   * Server-initiated signout metering caption
   * @type {string}
   * @default "signout_server_initiated"
   */
  FF_CLIENT_METER_SIGNOUT_SERVER: 'signout_server_initiated',

  /**
   * Successful auth ping metering caption
   * @type {string}
   * @default "ping_auth_success"
   */
  FF_CLIENT_METER_PING_SUCCESS: 'ping_auth_success',

  /**
   * Failed auth ping metering caption
   * @type {string}
   * @default "ping_auth_failure"
   */
  FF_CLIENT_METER_PING_FAILURE: 'ping_auth_failure',

  // ============================================
  // Internationalization (i18n) Configuration
  // ============================================

  /**
   * Current build mode for microcopy resolution
   * @type {string}
   * @default "LOCAL"
   * @description Standardized values: LOCAL, DEV, SIT, QA, PROD (UAT deprecated, use QA)
   */
  FF_SSE_I18N_BUILD_MODE: 'LOCAL',

  /**
   * Product identifier for microcopy resolution
   * @type {string}
   * @default "SSE"
   * @description StreamSyncEngage product identifier
   */
  FF_SSE_I18N_PRODUCT: 'SSE',

  /**
   * Whitelabel identifier for microcopy resolution
   * @type {string}
   * @default "M5T"
   * @description Whitelabel/brand identifier (M5T, custom client brands, etc.)
   */
  FF_SSE_I18N_WHITELABEL: 'M5T',

  /**
   * Current language code for microcopy resolution
   * @type {string}
   * @default "en"
   * @description ISO language code (en, es, zh-Hans, etc.)
   */
  FF_SSE_I18N_LANG: 'en',

  /**
   * Delay in milliseconds before applying i18n to DOM
   * @type {number}
   * @default 100
   * @description Delay to ensure DOM/Shadow DOM is fully hydrated before applying microcopy
   */
  FF_SSE_I18N_APPLY_DELAY_MS: 100,

  /**
   * Enable i18n debug logging
   * @type {boolean}
   * @default false
   */
  FF_SSE_I18N_DEBUG: false,

  /**
   * LocalStorage key for user language preference
   * @type {string}
   * @default "mvv_user_lang"
   */
  FF_SSE_LS_USER_LANG: 'mvv_user_lang',

  /**
   * LocalStorage key for whitelabel preference
   * @type {string}
   * @default "mvv_whitelabel"
   */
  FF_SSE_LS_WHITELABEL: 'mvv_whitelabel',
});

/**
 * Get feature flag value with optional override from environment variables
 *
 * @param {string} flagName - Name of the feature flag
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Feature flag value
 *
 * @example
 * const sessionKey = getFlag('FF_SSE_LS_SESSION_USER', 'session_user');
 */
function getFlag(flagName, defaultValue = null) {
  // Check if flag exists in registry
  if (flagName in FEATURE_FLAGS) {
    return FEATURE_FLAGS[flagName];
  }

  // Check environment variables (Node.js)
  if (typeof process !== 'undefined' && process.env && process.env[flagName]) {
    const envValue = process.env[flagName];

    // Parse boolean strings
    if (envValue === 'true') return true;
    if (envValue === 'false') return false;

    // Parse numeric strings
    if (!isNaN(envValue)) return Number(envValue);

    return envValue;
  }

  // Check Vite environment variables (Frontend)
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[flagName]) {
    const envValue = import.meta.env[flagName];

    // Parse boolean strings
    if (envValue === 'true') return true;
    if (envValue === 'false') return false;

    // Parse numeric strings
    if (!isNaN(envValue)) return Number(envValue);

    return envValue;
  }

  return defaultValue;
}

/**
 * Check if feature flag is enabled (boolean flags only)
 *
 * @param {string} flagName - Name of the feature flag
 * @returns {boolean} True if enabled, false otherwise
 *
 * @example
 * if (isEnabled('FF_SSE_FEATURE_SIGNUP_ENABLED')) {
 *   // Show signup button
 * }
 */
function isEnabled(flagName) {
  return getFlag(flagName, false) === true;
}

/**
 * Get all feature flags as a plain object
 *
 * @returns {Object} All feature flags
 */
function getAllFlags() {
  return { ...FEATURE_FLAGS };
}

/**
 * Export for both CommonJS (Node.js) and ES Modules (Frontend)
 */
// ES Modules (default)
export { FEATURE_FLAGS, getFlag, isEnabled, getAllFlags };

// CommonJS (Node.js)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    FEATURE_FLAGS,
    getFlag,
    isEnabled,
    getAllFlags
  };
}
